import type {
  PlatformData,
  SocialEngagementData,
  SocialPlatform,
} from "@/types/social-engagement";
import { ConfirmationDialog } from "@biom3/react";
import { Stack, Typography } from "@mui/material";
import type React from "react";
import { AnalyzingView } from "./AnalyzingView";
import { ErrorView } from "./ErrorView";

export const PLATFORM_CONFIG = {
  x: {
    name: "X",
    icon: "/images/quests/x.svg",
    getActivityText: (data: PlatformData) =>
      `${data.activityCount === 0 ? data.activityCount : data.activityCount.toString().padStart(2, "0")} Days ${data.targetSocialName ? `Tagging ${data.targetSocialName}` : "Posting on X"}`,
  },
  discord: {
    name: "Discord",
    icon: "/images/quests/discord.svg",
    getActivityText: (data: PlatformData) =>
      `${data.activityCount === 0 ? data.activityCount : data.activityCount.toString().padStart(2, "0")} Days Active on Discord`,
  },
  telegram: {
    name: "Telegram",
    icon: "/images/quests/telegram-logo.svg",
    getActivityText: (data: PlatformData) =>
      `${data.activityCount === 0 ? data.activityCount : data.activityCount.toString().padStart(2, "0")} Days Active on Telegram`,
  },
} as const;

const FormattedSocialEngagementCaption = ({
  gameName,
  rewardType,
  rank,
}: {
  gameName: string;
  rewardType: "points" | "gems";
  rank?: number;
}) => (
  <Stack spacing={2}>
    {typeof rank === "number" && (
      <Typography variant="body2" color="textSecondary">
        You are now ranked #{rank} on the {gameName} Leaderboard.
      </Typography>
    )}

    <Typography variant="body1" color="textSecondary" sx={{ fontWeight: 600 }}>
      Quality messages, more rewards!
    </Typography>
  </Stack>
);

const FormattedMultiplierCaption = ({
  gameName,
  rewardType,
  rank,
}: {
  gameName: string;
  rewardType: "points" | "gems";
  rank?: number;
}) => (
  <Stack spacing={2}>
    {typeof rank === "number" && (
      <Typography variant="body2" color="textPrimary">
        You are now ranked #{rank} on the {gameName} Leaderboard.
      </Typography>
    )}

    <Typography variant="body2" color="textSecondary">
      The more views your post gets, the more {rewardType} you earn - just hit
      the minimum view threshold to qualify. Bonus {rewardType} go to the best
      posts so get creative with reviews, gameplay clips and memes!
    </Typography>
  </Stack>
);

export const MODAL_CONTENT = {
  analyzing: {
    title: "Analysing your contributions...",
    caption: "How much did you participate so far?",
  },
  pointsSummary: {
    withConnections: {
      title: (points: number, rewardType: "points" | "gems") =>
        `You have ${points} ${rewardType} so far`,
      caption: (
        gameName: string,
        rewardType: "points" | "gems",
        rank?: number,
      ) => (
        <FormattedSocialEngagementCaption
          gameName={gameName}
          rewardType={rewardType}
          rank={rank}
        />
      ),
    },
    withConnectionsMultiplier: {
      title: (points: number, rewardType: "points" | "gems") =>
        `You have ${points} ${rewardType} so far`,
      caption: (
        gameName: string,
        rewardType: "points" | "gems",
        rank?: number,
      ) => (
        <FormattedMultiplierCaption
          gameName={gameName}
          rewardType={rewardType}
          rank={rank}
        />
      ),
    },
  },
  error: {
    title: "Something went wrong",
    caption:
      "We couldn't analyze your social engagement data right now. Please try again. If the problem persists please contact support",
  },
} as const;

export const getPlatformIcon = (platform: SocialPlatform): string => {
  return PLATFORM_CONFIG[platform].icon;
};

export const getPlatformName = (platform: SocialPlatform): string => {
  return PLATFORM_CONFIG[platform].name;
};

export const getPlatformActivityText = (
  platform: SocialPlatform,
  platformData: PlatformData,
): string => {
  return PLATFORM_CONFIG[platform].getActivityText(platformData);
};

export interface ModalContentConfig {
  title: string;
  caption: React.ReactNode;
  body: React.ReactNode;
  actions: React.ReactNode;
}

export type SocialEngagementModalState =
  | "analyzing"
  | "points_summary"
  | "error";

interface ModalConfigParams {
  modalState: SocialEngagementModalState;
  data: SocialEngagementData;
  gameId: string;
  gameName: string;
  userLeaderboardRank?: number;
  onClose: () => void;
  onConnect: (platform: SocialPlatform) => void;
  retryAnalysis: () => void;
  rewardType: "points" | "gems";
  hasMultiplier?: boolean;
  questId?: string;
}

export const getModalConfig = ({
  modalState,
  data,
  gameId,
  gameName,
  userLeaderboardRank,
  onClose,
  onConnect,
  retryAnalysis,
  rewardType,
  hasMultiplier,
  questId,
}: ModalConfigParams): ModalContentConfig => {
  switch (modalState) {
    case "analyzing":
      return {
        title: MODAL_CONTENT.analyzing.title,
        caption: MODAL_CONTENT.analyzing.caption,
        body: <AnalyzingView />,
        actions: null,
      };

    case "points_summary": {
      const summaryContent =
        hasMultiplier === true
          ? MODAL_CONTENT.pointsSummary.withConnectionsMultiplier
          : MODAL_CONTENT.pointsSummary.withConnections;

      return {
        title: summaryContent.title(data.totalPoints, rewardType),
        caption: summaryContent.caption(
          gameName,
          rewardType,
          userLeaderboardRank,
        ),
        body: (
          <div className="text-lg space-y-8">
            <div>
              Earn points by posting high-quality, helpful, entertaining, or
              relevant content — think bug reports, memes, insights, or genuine
              contributions to the conversation!
            </div>

            {questId?.toLowerCase().includes("discord") ? (
              <div className="bg-white/5 rounded-lg p-6 border border-white/20">
                <div className="space-y-3 text-left">
                  <p
                    className="text-left text-xs"
                    style={{ letterSpacing: "0.1rem", color: "#E0E0E0" }}
                  >
                    ALGORITHM UPDATED
                  </p>
                  <p
                    className="text-sm leading-relaxed text-left"
                    style={{ color: "#E0E0E0" }}
                  >
                    Our social analysis algorithm has been enhanced to better
                    detect low-quality content, including AI-generated messages.
                  </p>
                  <p
                    className="text-sm leading-relaxed text-left"
                    style={{ color: "#E0E0E0" }}
                  >
                    Repeat offenses will result in suspension from the perpetual
                    rewards weekly draws.
                  </p>
                </div>
              </div>
            ) : null}
          </div>
        ),
        actions: (
          <ConfirmationDialog.Content.Button onClick={onClose} size="large">
            Close
          </ConfirmationDialog.Content.Button>
        ),
      };
    }

    case "error":
      return {
        title: MODAL_CONTENT.error.title,
        caption: "",
        body: <ErrorView error={MODAL_CONTENT.error.caption} />,
        actions: (
          <>
            <ConfirmationDialog.Content.Button
              variant="primary"
              size="large"
              onClick={retryAnalysis}
              sx={{ width: "100%" }}
            >
              Try Again
            </ConfirmationDialog.Content.Button>
            <ConfirmationDialog.Content.Button
              variant="secondary"
              size="large"
              onClick={onClose}
              sx={{ width: "100%" }}
            >
              Close
            </ConfirmationDialog.Content.Button>
          </>
        ),
      };
  }
};
